import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import type { McpUsageTop } from "../api/types.js";
import { count } from "../format.js";
import { ErrorNote, Panel, ScreenHeader, Spinner, Table, Td, Th } from "../ui.js";

/**
 * MCP usage (owner 2026-10-12: "we could not tell how many people use MCP"). The MCP server posts counts every 60 s
 * to the API, which adds them to `mcp_usage_daily`; nothing that identifies a caller is stored.
 *
 * "Distinct callers" is approximate: the MCP process tells callers apart by salted hashes it keeps in memory for the
 * UTC day, so a restart (a deploy) counts a returning caller again that day.
 */
export function McpUsageScreen() {
  const usage = useResource((signal) => {
    void signal;
    return adminReads.mcpUsage(30);
  }, []);

  return (
    <>
      <ScreenHeader
        title="MCP usage"
        lead="Last 30 UTC days of this network (the MCP server reports Nile traffic to the Nile API). Connects are initialize requests; calls are tool calls. Distinct callers are approximate: an MCP server restart counts a returning caller again that day."
      />
      {usage.loading ? (
        <Spinner />
      ) : usage.error !== null ? (
        <ErrorNote error={usage.error} onRetry={() => usage.reload()} />
      ) : usage.data == null ? null : (
        <div class="space-y-3">
          <Panel title="Per day" subtitle="Tool calls by how the caller connected: anonymous, API key, or OAuth (a connected account).">
            <Table
              rows={usage.data.data.length}
              empty="No MCP traffic recorded in the last 30 days."
              head={
                <tr>
                  <Th>Day (UTC)</Th>
                  <Th numeric>Connects</Th>
                  <Th numeric>Calls</Th>
                  <Th numeric>Errors</Th>
                  <Th numeric>Distinct callers</Th>
                  <Th numeric>Anonymous</Th>
                  <Th numeric>API key</Th>
                  <Th numeric>OAuth</Th>
                </tr>
              }
            >
              {usage.data.data.map((row) => (
                <tr key={row.day}>
                  <Td>{row.day}</Td>
                  <Td numeric>{count(row.connects)}</Td>
                  <Td numeric>{count(row.calls)}</Td>
                  <Td numeric>{count(row.errors)}</Td>
                  <Td numeric>{count(row.distinct_callers)}</Td>
                  <Td numeric>{count(row.calls_by_auth.anonymous)}</Td>
                  <Td numeric>{count(row.calls_by_auth.key)}</Td>
                  <Td numeric>{count(row.calls_by_auth.oauth)}</Td>
                </tr>
              ))}
            </Table>
          </Panel>
          <div class="grid gap-3 lg:grid-cols-2">
            <Panel
              title="Top clients"
              subtitle="The MCP client's own name on connect; on tool calls the User-Agent product. Connects and calls."
            >
              <TopTable rows={usage.data.top_clients} label="Client" empty="No clients recorded." />
            </Panel>
            <Panel title="Top tools" subtitle="Tool calls over the 30 days.">
              <TopTable rows={usage.data.top_tools} label="Tool" empty="No tool calls recorded." />
            </Panel>
          </div>
        </div>
      )}
    </>
  );
}

function TopTable({ rows, label, empty }: { rows: McpUsageTop[]; label: string; empty: string }) {
  return (
    <Table
      rows={rows.length}
      empty={empty}
      head={
        <tr>
          <Th>{label}</Th>
          <Th numeric>Calls</Th>
          <Th numeric>Errors</Th>
          <Th numeric>Distinct callers</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.name}>
          <Td>{row.name}</Td>
          <Td numeric>{count(row.calls)}</Td>
          <Td numeric>{count(row.errors)}</Td>
          <Td numeric>{count(row.distinct_callers)}</Td>
        </tr>
      ))}
    </Table>
  );
}
